<template>
  <div>
    <div
      class="center-screen flex align-items-center justify-content-end border-bottom-1 border-400 pb-2"
    >
      <Button label="Nueva Notificación" @click="handleCreateNotification" v-if="isNotificante" />
    </div>
    <div class="center-screen">
      <NotificacionFilters @filtrar="handleFiltrar" :isNotificante="isNotificante" />
    </div>
    <DataTable
      :value="allNotifications"
      :lazy="true"
      :loading="loading"
      class="center-screen mt-5"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :rowsPerPageOptions="[10, 20, 30]"
      :paginator="true"
      :totalRecords="totalRows"
      :first="offset"
      :rows="rows"
      @page="handlePageChange"
      :sortField="sortField"
      :sortOrder="sortOrder"
      @sort="handleSort"
    >
      <template #empty>
        {{
          filtrosAplicados
            ? "No se encontraron notificaciones con los filtros seleccionados."
            : "Aún no hay notificaciones registradas."
        }}
      </template>
      <Column field="FechaCreacion" header="Fecha de creación" sortable />
      <Column field="FechaRespuesta" header="Fecha de respuesta" sortable>
        <template #body="slotProps">
          {{
            slotProps.data?.FechaRespuesta == undefined || slotProps.data?.FechaRespuesta == null
              ? "-"
              : slotProps.data.FechaRespuesta
          }}
        </template>
      </Column>
      <Column field="NumeroLegajo" header="Legajo" />
      <Column field="NombreVinculado" header="Nombre" />
      <Column field="DepartamentalNombre" header="Departamental" v-if="isNotificante" />
      <Column field="Estado" header="Estado">
        <template #body="slotProps">
          <Tag
            :value="estadoNoti(slotProps.data.Estado)"
            :style="{
              backgroundColor: colorActive(estadoNoti(slotProps.data.Estado)),
              fontSize: '15px',
              width: '125px',
              color: 'white',
            }"
          />
        </template>
      </Column>
      <Column>
        <template #body="slotProps">
          <Button
            type="button"
            icon="pi pi-eye"
            @click="handleViewNotification(slotProps.data)"
            class="p-button-rounded p-button-outlined"
          />
        </template>
      </Column>
      <template #paginatorstart>
        <div class="flex align-items-center" v-if="getLimit != undefined">
          <b>
            <span class="pagination">{{
              `Mostrando registros del ${offset} al ${
                offset + (getLimitByDropDown === null ? getLimit : getLimitByDropDown) > totalRows
                  ? totalRows
                  : offset + (getLimitByDropDown === null ? getLimit : getLimitByDropDown)
              } de un total de ${totalRows}`
            }}</span>
          </b>
        </div>
      </template>
      <template #paginatorend />
    </DataTable>
    <ModalNotificacion
      :visible="modalVisible"
      :modo="modalModo"
      :notificacion="notificacionSeleccionada"
      @close="() => (modalVisible = false)"
      @getNotifications="getNotifications"
      @updateModo="updateModo"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, defineComponent } from "vue";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useToast } from "@/composables/useToast";
import { useRest } from "@/composables/useRest";
import ModalNotificacion from "@/components/notificaciones(user)/ModalNotificacion.vue";
import NotificacionFilters from "@/components/notificaciones(user)/NotificacionFilters.vue";
import { useNotificationStore } from "@/store/Pinia/useNotificationStore";

export default defineComponent({
  name: "Notifications",
  components: {
    ModalNotificacion,
    NotificacionFilters,
  },
  setup() {
    const store = useStoreCompat();
    const notificationStore = useNotificationStore();
    const toast = useToast();
    const { get } = useRest();

    const allNotifications = ref([]);
    const filtrosActivos = ref(null);
    const filtrosAplicados = ref(false);
    const loading = ref(true);
    const modalVisible = ref(false);
    const modalModo = ref("crear");
    const notificacionSeleccionada = ref(null);
    const totalRows = ref(0);
    const offset = ref(0);
    const rows = ref(10);
    const page = ref(1);
    const getLimit = ref(10);
    const getLimitByDropDown = ref(null);

    const departamentales = computed(() => store.state.siis.departamentales);
    const user = computed(() => store.state.user.userdata);
    const departamentalID = computed(() =>
      user.value?.DepartamentalID ? user.value?.DepartamentalID : user.value?.departamentalID
    );
    const isNotificante = computed(() => user.value?.tipoUsuario === "Notificante");

    const sortField = ref(isNotificante.value ? "FechaRespuesta" : "FechaCreacion");
    const sortOrder = ref(-1); // 1 para ASC, -1 para DESC

    const ordenActual = computed(
      () => `${sortField.value}+${sortOrder.value === 1 ? "ASC" : "DESC"}`
    );

    const getNotifications = async () => {
      loading.value = true;
      const limit = getLimitByDropDown.value || getLimit.value || 10;
      const offsetParam = offset.value || 0;
      totalRows.value = await getTotalRows();
      let getOptions;
      /**
       *  Posible vulnerabilidad, habría que hacer este filtro en el back
       *  -> si el usuario es Notificante(todas las notis)
       *  -> si es notificante-departamental(solo la de su departamental)
       * */
      if (!isNotificante.value) {
        const search = {
          DepartamentalID: [{ operator: "=", value: departamentalID.value }],
        };
        const query = encodeURIComponent(JSON.stringify(search));
        getOptions = {
          url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones/?search=${query}&limit=${limit}&offset=${offsetParam}&order=${ordenActual.value}`,
        };
      } else {
        getOptions = {
          url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones?limit=${limit}&offset=${offsetParam}&order=${ordenActual.value}`,
        };
      }

      try {
        const response = await get(getOptions);
        allNotifications.value = response.data.data;
        if (!isNotificante.value) {
          await cargarPendientes();
        }
      } finally {
        loading.value = false;
      }
    };

    const cargarPendientes = async () => {
      await notificationStore.getPendientes(departamentalID.value);
    };

    const colorActive = (state) => {
      switch (state) {
        case "Enviado":
          return "#74DCFF";
        case "Respondido":
          return "#22C55E";
        case "Finalizado":
          return "#D9D9D9";
        case "Pendiente":
          return "#D32F2F";
      }
    };

    const estadoNoti = (estado) => {
      if (!isNotificante.value && estado === "Enviado") {
        return "Pendiente";
      }
      return estado;
    };

    const handleFiltrar = async (filtros) => {
      loading.value = true;
      filtrosAplicados.value = true;
      filtrosActivos.value = filtros;
      offset.value = 0;
      try {
        let search = buildQueryFilter(filtros);
        if (!isNotificante.value) {
          search = {
            ...search,
            DepartamentalID: [{ operator: "=", value: departamentalID.value }],
          };
        }

        const query = encodeURIComponent(JSON.stringify(search));
        const { data } = await get({
          url: `/notificaciones/?search=${query}&limit=${rows.value}&offset=${offset.value}&order=${ordenActual.value}`,
        });
        allNotifications.value = data.data;
        totalRows.value = await getTotalRows(search);
      } catch (error) {
        console.log(error);
        toast.error("Error al aplicar filtros.");
      } finally {
        loading.value = false;
      }
    };

    const buildQueryFilter = (filtros) => {
      const search = {};
      if (filtros.Estado) search.Estado = [{ operator: "=", value: filtros.Estado }];
      if (filtros.DepartamentalID)
        search.DepartamentalID = [{ operator: "=", value: filtros.DepartamentalID }];
      if (filtros.FechaCreacionDesde && filtros.FechaCreacionHasta) {
        search.FechaCreacion = [
          { operator: ">=", value: filtros.FechaCreacionDesde },
          { operator: "<=", value: filtros.FechaCreacionHasta },
        ];
      } else if (filtros.FechaCreacionDesde) {
        search.FechaCreacion = [{ operator: ">=", value: filtros.FechaCreacionDesde }];
      } else if (filtros.FechaCreacionHasta) {
        search.FechaCreacion = [{ operator: "<=", value: filtros.FechaCreacionHasta }];
      }
      if (filtros.FechaRespuestaDesde && filtros.FechaRespuestaHasta) {
        search["exp_pre_RespuestaNotificacion.FechaCreacion"] = [
          { operator: ">=", value: filtros.FechaRespuestaDesde },
          { operator: "<=", value: filtros.FechaRespuestaHasta },
        ];
      } else if (filtros.FechaRespuestaDesde) {
        search["exp_pre_RespuestaNotificacion.FechaCreacion"] = [
          { operator: ">=", value: filtros.FechaRespuestaDesde },
        ];
      } else if (filtros.FechaRespuestaHasta) {
        search["exp_pre_RespuestaNotificacion.FechaCreacion"] = [
          { operator: "<=", value: filtros.FechaRespuestaHasta },
        ];
      }
      return search;
    };

    const handleCreateNotification = () => {
      modalModo.value = "crear";
      notificacionSeleccionada.value = null;
      modalVisible.value = true;
    };

    const updateModo = (val) => {
      modalModo.value = val;
    };

    const handleViewNotification = async (notificacion) => {
      const notiID = notificacion.NotificacionID;
      modalModo.value = "ver";
      modalVisible.value = true;
      notificacionSeleccionada.value = { isLoading: true };

      try {
        const resNoti = await get({
          url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones/${notiID}`,
        });
        const noti = resNoti.data.data[0];
        noti.archivos = [await fetchArchivo(notiID, "NotificacionID")];

        if (noti.FechaRespuesta && noti.RespuestaNotificacion) {
          const archivoRespuesta = await fetchArchivo(
            noti.RespuestaNotificacion,
            "RespuestaNotificacionID"
          );
          noti.archivos.push(archivoRespuesta);
        }

        notificacionSeleccionada.value = noti;
      } catch (error) {
        toast.error("Error al obtener la notificación o archivo");
      }
    };

    const fetchArchivo = async (id, paramName) => {
      const searchParam = encodeURIComponent(
        JSON.stringify({
          [paramName]: [{ operator: "=", value: id }],
        })
      );
      const fileResponse = await get({
        url: `/files/notificacion/?search=${searchParam}`,
        responseType: "blob",
      });
      const blob = fileResponse.data ?? fileResponse;
      return {
        nombre: `archivo_${id}.pdf`,
        url: URL.createObjectURL(blob),
      };
    };

    const handlePageChange = async (event) => {
      offset.value = event.first;
      rows.value = event.rows;
      getLimitByDropDown.value = event.rows;
      page.value = event.page + 1;
      loading.value = true;
      try {
        if (filtrosAplicados.value && filtrosActivos.value) {
          let search = buildQueryFilter(filtrosActivos.value);
          if (!isNotificante.value) {
            search = {
              ...search,
              DepartamentalID: [{ operator: "=", value: departamentalID.value }],
            };
          }
          const query = encodeURIComponent(JSON.stringify(search));
          const { data } = await get({
            url: `/notificaciones/?search=${query}&limit=${rows.value}&offset=${offset.value}&order=${ordenActual.value}`,
          });
          allNotifications.value = data.data;
        } else {
          await getNotifications();
        }
      } finally {
        loading.value = false;
      }
    };

    const getTotalRows = async (search = null) => {
      try {
        let getOptions;

        if (!isNotificante.value) {
          const searchObj = {
            ...(search || {}),
            DepartamentalID: [{ operator: "=", value: departamentalID.value }],
          };
          const query = encodeURIComponent(JSON.stringify(searchObj));
          getOptions = {
            url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones/?search=${query}`,
            params: { getTotal: true },
          };
        } else {
          if (search) {
            const query = encodeURIComponent(JSON.stringify(search));
            getOptions = {
              url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones/?search=${query}`,
              params: { getTotal: true },
            };
          } else {
            getOptions = {
              url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/notificaciones`,
              params: { getTotal: true },
            };
          }
        }
        const response = await get(getOptions);
        return parseInt(response.data.doc?.total || 0);
      } catch (e) {
        console.error(e);
        return 0;
      }
    };

    onMounted(async () => {
      await getNotifications();
      store.dispatch("siis/setDepartamentales");
    });

    const handleSort = async (e) => {
      sortField.value = e.sortField;
      sortOrder.value = e.sortOrder;
      if (filtrosAplicados.value && filtrosActivos.value) {
        await handleFiltrar(filtrosActivos.value);
      } else {
        await getNotifications();
      }
    };

    return {
      allNotifications,
      filtrosAplicados,
      loading,
      modalVisible,
      modalModo,
      notificacionSeleccionada,
      departamentales,
      isNotificante,
      offset,
      rows,
      getLimit,
      getLimitByDropDown,
      filtrosActivos,
      page,
      totalRows,
      sortField,
      sortOrder,
      colorActive,
      estadoNoti,
      getNotifications,
      handleFiltrar,
      handleCreateNotification,
      handleViewNotification,
      updateModo,
      buildQueryFilter,
      getTotalRows,
      handlePageChange,
      cargarPendientes,
      handleSort,
    };
  },
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.center-screen {
  width: 90%;
  margin: 0 auto 1rem auto;
}
>>> .p-datatable .p-column-header-content {
  justify-content: center;
}
>>> .p-datatable .p-datatable-tbody > tr > td {
  text-align: center;
}
</style>
